import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast'; 

interface SearchSuggestion {
  type: 'city' | 'zip';
  value: string;
  display: string;
  slug?: string;
  stateCode?: string;
}

export function CitySearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const getSuggestions = async () => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const isNumeric = /^\d+$/.test(searchQuery);
        
        if (isNumeric) {
          // Search for ZIP codes
          const { data: cities } = await supabase
            .from('cities')
            .select('name, zip_codes, slug, state:state_id(code)')
            .filter('zip_codes', 'cs', `{${searchQuery}}`);

          const zipSuggestions = cities?.flatMap(city => 
            city.zip_codes
              .filter(zip => zip.startsWith(searchQuery))
              .map(zip => ({
                type: 'zip' as const,
                value: zip,
                display: `${zip} - ${city.name}, ${city.state.code}`
              }))
          ) || [];

          // Remove duplicates based on value
          setSuggestions(Array.from(new Map(zipSuggestions.map(s => [s.value, s])).values()));
        } else {
          // Search for cities
          const { data: cities } = await supabase
            .from('cities')
            .select('name, state:state_id(code), zip_codes, slug')
            .ilike('name', `${searchQuery}%`)
            .limit(5);

          const citySuggestions = cities?.map(city => ({
            type: 'city' as const,
            value: city.zip_codes[0],
            display: `${city.name}, ${city.state.code}`,
            slug: city.slug,
            stateCode: city.state.code
          })) || [];

          // Remove duplicates based on display
          setSuggestions(Array.from(new Map(citySuggestions.map(s => [s.display, s])).values()));
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    };

    const timeoutId = setTimeout(getSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setShowSuggestions(false);
    setShowSuggestions(false);
    
    const isNumeric = /^\d+$/.test(searchQuery);
    if (isNumeric && searchQuery.length === 5) {
      // Get city and state info for the ZIP code
      const { data: cities } = await supabase
        .from('cities')
        .select(`
          name,
          slug,
          state:state_id(code)
        `)
        .filter('zip_codes', 'cs', `{${searchQuery}}`);

      if (cities && cities.length > 0) {
        // Use the first matching city
        const city = cities[0];
        navigate(`/${city.state.code.toLowerCase()}/${city.slug}/${searchQuery}`);
      } else {
        toast.error('ZIP code not found in our service areas');
      }
    } else {
      // Find matching city and use its first ZIP code
      const { data: cities } = await supabase
        .from('cities')
        .select(`
          name,
          zip_codes,
          slug,
          state:state_id!inner(code)
        `)
        .ilike('name', searchQuery)
        .limit(1);

      if (cities && cities.length > 0) {
        navigate(`/${cities[0].state.code.toLowerCase()}/${cities[0].slug}/${cities[0].zip_codes[0]}`);
      } else {
        toast.error('Please enter a valid city name or ZIP code');
      }
    }
    
    setLoading(false);
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    if (suggestion.type === 'city') {
      // For city suggestions
      const { stateCode, slug, value } = suggestion;
      if (stateCode && slug && value) {
        navigate(`/${stateCode.toLowerCase()}/${slug}/${value}`);
        setSearchQuery('');
        setShowSuggestions(false);
      }
    } else {
      // For ZIP code suggestions, get city info and navigate
      const { value } = suggestion;
      if (value) {
        supabase
          .from('cities')
          .select('slug, state:state_id(code)')
          .filter('zip_codes', 'cs', `{${value}}`)
          .single()
          .then(({ data, error }) => {
            if (error) {
              console.error('Error getting city info:', error);
              toast.error('Error finding location');
              return;
            }
            if (data) {
              navigate(`/${data.state.code.toLowerCase()}/${data.slug}/${value}`);
              setSearchQuery('');
              setShowSuggestions(false);
            }
          });
      }
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto">
      <div className="relative">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSuggestions(true);
              }}
              placeholder="Enter city name or ZIP code"
              className="w-full px-6 py-4 text-lg rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={loading || !searchQuery}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              <Search className="w-5 h-5 mr-2" />
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg transition-colors"
                title={`Search plumbers in ${suggestion.display}`}
              >
                {suggestion.display}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}